import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { BsGear, BsBarChart, BsInbox, BsArrowClockwise } from "react-icons/bs";
import { Skeleton } from "../../Components/Needed";
import ticketApi from "../../Helpers/ticketApi";
import { PRIORITY_CLASS, PRIORITY_LABEL, STATUS_CLASS, assigneeText, raiserText, slaSummary, when } from "./shared";
import "../../Assets/css/tickets.css";

// Admin ticket queues (Phase 7.8).
const QUEUES = [
    { key: "pool", label: "Pool", hint: "Customer tickets nobody has taken yet." },
    { key: "appealed", label: "Appealed", hint: "Sent to the admins by staff, or escalated when nobody took them in time." },
    { key: "mine", label: "Mine", hint: "Open tickets assigned to you." },
    { key: "breached", label: "SLA breached", hint: "Open tickets that missed a target." },
    { key: "all", label: "All customer tickets", hint: "Every customer ticket." },
    { key: "staff", label: "Staff tickets", hint: "Raised by staff. Only admins handle them." },
];
const STATUSES = ["", "Open", "In Progress", "Waiting on customer", "Resolved", "Closed"];
const LIMIT = 20;

const TicketQueues = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const queue = QUEUES.some((q) => q.key === params.get("queue")) ? params.get("queue") : "pool";
    const status = params.get("status") || "";
    const page = Math.max(Number(params.get("page")) || 0, 0);
    const [search, setSearch] = useState(params.get("search") || "");
    const [data, setData] = useState(null);
    const [counts, setCounts] = useState({});

    const set = (patch) => {
        const next = new URLSearchParams(params);
        Object.entries(patch).forEach(([k, v]) => (v === "" || v === null || v === undefined ? next.delete(k) : next.set(k, String(v))));
        setParams(next, { replace: true });
    };

    const load = useCallback(async () => {
        setData(null);
        const [list, c] = await Promise.all([
            ticketApi.queue(queue, { page, limit: LIMIT, ...(status ? { status } : {}), ...(params.get("search") ? { search: params.get("search") } : {}) }),
            ticketApi.counts(),
        ]);
        if (!list.ok) { toast.error(list.message); setData({ rows: [], total: 0 }); } else setData(list.data);
        if (c.ok) setCounts(c.data.counts || {});
    }, [queue, status, page, params]);
    useEffect(() => { load(); }, [load]);

    // A quiet refresh every 30 seconds keeps the queues current.
    useEffect(() => {
        const timer = setInterval(() => { ticketApi.counts().then((c) => c.ok && setCounts(c.data.counts || {})); }, 30000);
        return () => clearInterval(timer);
    }, []);

    const total = data?.total || 0;
    const pages = Math.max(1, Math.ceil(total / LIMIT));
    const current = QUEUES.find((q) => q.key === queue);

    return (
        <div className="settings-page tk-page">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <h4 className="fw-semibold mb-1">Support tickets</h4>
                    <p className="text-muted mb-0">Customer tickets handled by hub staff, appeals and escalations, and tickets raised by staff.</p>
                </div>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary d-inline-flex align-items-center gap-2" onClick={() => navigate("/tickets/reports")}><BsBarChart aria-hidden="true" /> Reports</button>
                    <button type="button" className="btn btn-outline-secondary d-inline-flex align-items-center gap-2" onClick={() => navigate("/tickets/settings")}><BsGear aria-hidden="true" /> Settings</button>
                </div>
            </div>

            <div className="tk-tabs" role="tablist" aria-label="Queues">
                {QUEUES.map((q) => (
                    <button key={q.key} type="button" role="tab" aria-selected={queue === q.key} className={`tk-tab ${queue === q.key ? "is-active" : ""}`} onClick={() => set({ queue: q.key, page: "", status: "" })}>
                        {q.label}
                        {counts[q.key] !== undefined && q.key !== "all" ? <span className={`tk-count ${q.key === "breached" && counts[q.key] ? "is-bad" : ""}`}>{counts[q.key]}</span> : null}
                    </button>
                ))}
            </div>
            <p className="small text-muted mt-2 mb-3">{current.hint}</p>

            <div className="d-flex flex-wrap gap-2 mb-3">
                <form className="d-flex gap-2" onSubmit={(e) => { e.preventDefault(); set({ search: search.trim(), page: "" }); }} role="search">
                    <label className="visually-hidden" htmlFor="tk-search">Search tickets</label>
                    <input id="tk-search" className="form-control form-control-sm" style={{ minWidth: 240 }} placeholder="Ticket ID, subject, booking, raiser" value={search} onChange={(e) => setSearch(e.target.value)} />
                    <button type="submit" className="btn btn-sm btn-dark">Search</button>
                </form>
                {["all", "staff"].includes(queue) ? (
                    <select className="form-select form-select-sm w-auto" aria-label="Status" value={status} onChange={(e) => set({ status: e.target.value, page: "" })}>
                        {STATUSES.map((s) => <option key={s || "any"} value={s}>{s || "Any status"}</option>)}
                    </select>
                ) : null}
                <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={load} aria-label="Refresh"><BsArrowClockwise aria-hidden="true" /></button>
            </div>

            {!data ? <Skeleton height={260} /> : data.rows.length === 0 ? (
                <div className="text-center text-muted py-5 border rounded-3 bg-white">
                    <BsInbox size={28} className="mb-2" aria-hidden="true" />
                    <div>Nothing here{params.get("search") ? " for that search" : ""}.</div>
                </div>
            ) : (
                <div className="table-responsive bg-white rounded-3 border">
                    <table className="table table-hover align-middle mb-0 tk-table">
                        <thead>
                            <tr>
                                <th scope="col">Ticket</th>
                                <th scope="col">Raised by</th>
                                <th scope="col">Status</th>
                                <th scope="col">With</th>
                                <th scope="col">SLA</th>
                                <th scope="col">Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((t) => {
                                const sla = slaSummary(t);
                                return (
                                    <tr key={t.ticketId} role="link" tabIndex={0} onClick={() => navigate(`/tickets/${t.ticketId}`)} onKeyDown={(e) => { if (e.key === "Enter") navigate(`/tickets/${t.ticketId}`); }}>
                                        <td>
                                            <div className="fw-semibold">{t.subject}{t.unread?.handler ? <span className="tk-dot" title="New message from the raiser" /> : null}</div>
                                            <div className="small text-muted">{t.ticketId} · {t.category}{t.reason ? ` / ${t.reason}` : ""}{t.bookingId ? ` · ${t.bookingId}` : ""}</div>
                                        </td>
                                        <td className="small">{raiserText(t)}</td>
                                        <td>
                                            <span className={`tk-status ${STATUS_CLASS[t.status] || ""}`}>{t.status}</span>
                                            <span className={`tk-priority ${PRIORITY_CLASS[t.priority] || ""}`}>{PRIORITY_LABEL[t.priority]}</span>
                                        </td>
                                        <td className="small">{assigneeText(t)}</td>
                                        <td className="small">{sla ? <span className={`tk-sla tk-sla-${sla.tone}`}>{sla.text}</span> : "-"}</td>
                                        <td className="small text-muted">{when(t.updatedAt)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {total > LIMIT ? (
                <nav className="d-flex justify-content-between align-items-center mt-3" aria-label="Pages">
                    <span className="small text-muted">{total} tickets</span>
                    <div className="btn-group btn-group-sm">
                        <button type="button" className="btn btn-outline-secondary" disabled={page === 0} onClick={() => set({ page: page - 1 || "" })}>Previous</button>
                        <span className="btn btn-outline-secondary disabled">{page + 1} / {pages}</span>
                        <button type="button" className="btn btn-outline-secondary" disabled={page + 1 >= pages} onClick={() => set({ page: page + 1 })}>Next</button>
                    </div>
                </nav>
            ) : null}
        </div>
    );
};

export default TicketQueues;
